import React, { useEffect, useRef, useState } from "react";
import { auth } from "./firebaseConfig";
import { createUserWithEmailAndPassword, updateProfile } from "firebase/auth";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "./firebaseConfig";
import { useNavigate } from "react-router-dom";
import { rememberPendingUsername } from "./utils/pendingUsername";
import "./Login.css";
import "./Signup.css";
import StudyDenLogo from "./assets/images/branding/Studyden_Primary.svg";
import StudyDenWhiteBg from "./assets/images/branding/Studyden_Primary.svg";

// Inline, actionable messages for account-creation failures. `field` marks the
// input the student should correct, when there is one.
const signUpError = (error) => {
  switch (error?.code) {
    case "auth/email-already-in-use":
      return { message: "An account already exists for this email. Sign in instead, or use a different email.", field: "email" };
    case "auth/invalid-email":
      return { message: "Enter a valid email address.", field: "email" };
    case "auth/weak-password":
    case "auth/password-does-not-meet-requirements":
      return { message: "Choose a stronger password (at least 6 characters).", field: "password" };
    case "auth/network-request-failed":
      return { message: "We couldn't reach StudyDen. Check your connection and try again." };
    case "auth/too-many-requests":
      return { message: "Too many attempts. Please wait a moment and try again." };
    case "auth/operation-not-allowed":
      return { message: "Email sign-up isn't available right now. Please contact support." };
    default:
      return { message: "We couldn't create your account. Please try again." };
  }
};

const SignUp = () => {
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState(null); // { message, field }
  const submittingRef = useRef(false);
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, []);

  const handleSignUp = async (e) => {
    e.preventDefault();
    if (submittingRef.current) return;
    setFormError(null);

    if (password !== confirmPassword) {
      setFormError({ message: "Passwords do not match. Re-enter them and try again.", field: "confirm" });
      return;
    }

    submittingRef.current = true;
    setIsSubmitting(true);

    let user;
    try {
      ({ user } = await createUserWithEmailAndPassword(auth, email, password));
    } catch (error) {
      console.error("Error during sign-up:", error.code || error.message);
      submittingRef.current = false;
      setIsSubmitting(false);
      setFormError(signUpError(error));
      return;
    }

    // The new session is now signed in. App owns the transition from here, as
    // it does for Login: it keeps the branded loading state mounted until the
    // authoritative profile snapshot arrives, then /signup redirects to the
    // account's destination (onboarding for a new, role-less account). This
    // component unmounts during that transition, so it must not navigate itself.
    const chosenUsername = username.trim() || username;
    // App can show onboarding while these writes are still in flight, and the
    // profile write can fail. Keep the chosen name where onboarding's backend
    // fallback can find it: in this tab for the onboarding request, and on the
    // Auth account. Neither waits for the other.
    rememberPendingUsername(user.uid, chosenUsername);
    const [profileWrite, nameWrite] = await Promise.allSettled([
      // Create a minimal document — do NOT assign role: "student" here.
      // Role is assigned by the backend after the user completes the onboarding screen.
      setDoc(doc(db, "users", user.uid), {
        username: chosenUsername,
        email: user.email,
        extraTimePercentage: 0,
        createdAt: serverTimestamp(),
      }),
      updateProfile(user, { displayName: chosenUsername }),
    ]);
    // Partial success: the account exists and is signed in, so the person must
    // not be asked to register again. Onboarding creates any missing profile
    // from the saved name.
    if (profileWrite.status === "rejected") {
      console.error("Error saving the new user profile:", profileWrite.reason?.code || profileWrite.reason?.message);
    }
    if (nameWrite.status === "rejected") {
      console.error("Error saving the new user display name:", nameWrite.reason?.code || nameWrite.reason?.message);
    }
  };

  const IconUser = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );

  const IconMail = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="m3 7 9 6 9-6" />
    </svg>
  );

  const IconLock = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="11" width="18" height="10" rx="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );

  const IconEyeOff = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
      <line x1="1" y1="1" x2="23" y2="23" />
      <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
    </svg>
  );

  const IconEye = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );

  const IconBook = () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 4.5A2.5 2.5 0 0 1 4.5 2H11v18H4.5A2.5 2.5 0 0 0 2 22.5z" />
      <path d="M22 4.5A2.5 2.5 0 0 0 19.5 2H13v18h6.5a2.5 2.5 0 0 1 2.5 2.5z" />
    </svg>
  );

  const IconClipboard = () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="4" y="4" width="16" height="18" rx="2" />
      <path d="M9 4V2h6v2" />
      <path d="m8 12 2 2 5-5" />
      <path d="M8 18h8" />
    </svg>
  );

  const IconProgress = () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 20V10" />
      <path d="M10 20V4" />
      <path d="M16 20v-7" />
      <path d="m3 8 6-5 6 6 6-6" />
      <path d="M17 3h4v4" />
    </svg>
  );

  const IconShield = () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  );

  const IconCloud = () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.5 19H6a4 4 0 0 1-.7-7.94A7 7 0 0 1 18.72 9.2 5 5 0 0 1 17.5 19z" />
    </svg>
  );

  const IconLearning = () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="m3 11 9-6 9 6-9 6z" />
      <path d="M7 14v4c3 2 7 2 10 0v-4" />
    </svg>
  );

  return (
    <div className="studyden-login studyden-signup">
      <main className="sd-auth-layout">
        <section className="sd-brand-panel" aria-labelledby="sd-signup-brand-heading">
          <div className="sd-brand-glow" aria-hidden="true" />
          <div className="sd-brand-content">
            <div className="sd-brand-logo-wrap">
              <img src={StudyDenLogo} alt="StudyDen — Learn. Assess. Progress." className="sd-brand-logo" />
            </div>
            <p className="sd-brand-positioning">AI-powered learning &amp; assessment</p>

            <div className="sd-brand-copy">
              <h1 id="sd-signup-brand-heading">Everything learning<br />in <span>one place.</span></h1>
              <p>
                StudyDen brings together learning, assessment, feedback and progress
                tracking in one intelligent, AI-powered platform for educators and learners.
              </p>
            </div>

            <div className="sd-features" aria-label="StudyDen platform benefits">
              <div className="sd-feature">
                <span className="sd-feature-icon" aria-hidden="true"><IconBook /></span>
                <div><h2>Learn</h2><p>Access and engage with rich learning content.</p></div>
              </div>
              <div className="sd-feature">
                <span className="sd-feature-icon" aria-hidden="true"><IconClipboard /></span>
                <div><h2>Assess</h2><p>Complete assignments and assessments with confidence.</p></div>
              </div>
              <div className="sd-feature">
                <span className="sd-feature-icon" aria-hidden="true"><IconProgress /></span>
                <div><h2>Progress</h2><p>Track performance and achieve your goals.</p></div>
              </div>
            </div>
          </div>
        </section>

        <section className="sd-form-panel" aria-labelledby="sd-signup-title">
          <div className="sd-form-wrap">
            <img src={StudyDenWhiteBg} alt="StudyDen — Learn. Assess. Progress." className="sd-mobile-logo" />
            <header className="sd-form-header">
              <p className="sd-eyebrow">Join StudyDen</p>
              <h2 id="sd-signup-title">Create your account</h2>
              <p>Get started with StudyDen today.</p>
            </header>

            <form className="sd-login-form sd-signup-form" onSubmit={handleSignUp} aria-busy={isSubmitting}>
              {formError && (
                <p className="sd-reset-message error sd-signup-error" id="signup-error" role="alert">
                  {formError.message}
                </p>
              )}
              <div className="sd-field">
                <label htmlFor="signup-username">Username</label>
                <div className="sd-input-wrap">
                  <span className="sd-input-icon" aria-hidden="true"><IconUser /></span>
                  <input
                    id="signup-username"
                    name="username"
                    type="text"
                    placeholder="Choose a username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    autoComplete="username"
                    maxLength={100}
                    required
                  />
                </div>
              </div>

              <div className="sd-field">
                <label htmlFor="signup-email">Email</label>
                <div className="sd-input-wrap">
                  <span className="sd-input-icon" aria-hidden="true"><IconMail /></span>
                  <input
                    id="signup-email"
                    name="email"
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email"
                    aria-invalid={formError?.field === "email" || undefined}
                    aria-describedby={formError?.field === "email" ? "signup-error" : undefined}
                    required
                  />
                </div>
              </div>

              <div className="sd-field">
                <label htmlFor="signup-password">Password</label>
                <div className="sd-input-wrap sd-signup-password-wrap">
                  <span className="sd-input-icon" aria-hidden="true"><IconLock /></span>
                  <input
                    id="signup-password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Create a password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="new-password"
                    aria-invalid={formError?.field === "password" || undefined}
                    aria-describedby={formError?.field === "password" ? "signup-error" : undefined}
                    required
                  />
                  <button
                    type="button"
                    className="sd-password-toggle"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    aria-pressed={showPassword}
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <IconEye /> : <IconEyeOff />}
                  </button>
                </div>
              </div>

              <div className="sd-field">
                <label htmlFor="signup-confirm-password">Confirm password</label>
                <div className="sd-input-wrap sd-signup-password-wrap">
                  <span className="sd-input-icon" aria-hidden="true"><IconLock /></span>
                  <input
                    id="signup-confirm-password"
                    name="confirm-password"
                    type={showConfirm ? "text" : "password"}
                    placeholder="Confirm your password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    autoComplete="new-password"
                    aria-invalid={formError?.field === "confirm" || undefined}
                    aria-describedby={formError?.field === "confirm" ? "signup-error" : undefined}
                    required
                  />
                  <button
                    type="button"
                    className="sd-password-toggle"
                    aria-label={showConfirm ? "Hide confirmed password" : "Show confirmed password"}
                    aria-pressed={showConfirm}
                    onClick={() => setShowConfirm(!showConfirm)}
                  >
                    {showConfirm ? <IconEye /> : <IconEyeOff />}
                  </button>
                </div>
              </div>

              <button type="submit" className="sd-primary-button" disabled={isSubmitting} aria-busy={isSubmitting}>
                {isSubmitting ? "Creating account..." : "Create account"}
              </button>
            </form>

            <p className="sd-signup-copy">
              Already have an account?{" "}
              <button type="button" onClick={() => navigate("/login")}>Sign in</button>
            </p>
          </div>
        </section>
      </main>

      <aside className="sd-trust-strip" aria-label="Why choose StudyDen">
        <div className="sd-trust-item">
          <span aria-hidden="true"><IconShield /></span>
          <div><h2>Secure &amp; Private</h2><p>Your data is safe and protected.</p></div>
        </div>
        <div className="sd-trust-item">
          <span aria-hidden="true"><IconCloud /></span>
          <div><h2>Accessible Anywhere</h2><p>Learn and assess from any device.</p></div>
        </div>
        <div className="sd-trust-item">
          <span aria-hidden="true"><IconLearning /></span>
          <div><h2>Built by Educators</h2><p>Designed by educators, for educators.</p></div>
        </div>
      </aside>

      <footer className="sd-footer">&copy; 2026 StudyDen. All rights reserved.</footer>
    </div>
  );
};

export default SignUp;
